/* eslint-disable jsx-a11y/label-has-associated-control */
import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Input, Loader, ModalGeneric, Switch, TextArea } from 'ypf-pas-general-ui-components';
import { TbCircleLetterP } from 'react-icons/tb';
import { useFormik } from 'formik';

import { capitalize, formatDateToSpanish, validateZerosInput } from '../../../utils/formatUtils';
import {
  approverRectifyALine,
  approverRectifyLineWithPP,
  cancelQuantity,
  getProgramPoints,
  rectifyALine,
  rectiyProgramPoint
} from '../../../services/workReport';
import useToast from '../../../hooks/useToast';
import '../WorkReportModals.scss';
import './EditQuantityModal.scss';
import { checkRol } from '../../../security';
import { inspector, aprobador } from '../../../security/constantsRols';
import LoaderContext from '../../../context/LoaderProvider';
import ProgramPointList from '../ProgramPointModal/ProgramPointList/ProgramPointList';
import ComponentWithTooltip from '../../ComponentWithTooltip/ComponentWithTooltip';
import { ubicationTypeEnum } from '../../../utils/dataStatus';
import adapterDefaultValueCS from '../../../adapters/defaultValueCodeSubCode/defaultValueCSAdapter';

const validationSchema = Yup.object().shape({
  quantity: Yup.number(),
  newQuantity: Yup.number(),
  requireAccordance: Yup.bool().required(),
  requestAttachFile: Yup.bool().required(),
  comment: Yup.string(),
  isPP: Yup.bool(),
  programPoints: Yup.array().when('isPP', {
    is: true,
    then: Yup.array()
      .test('programPoints', 'Validación de puntos de programa', (array) => {
        if (array.length === 0) return false; // Si el arreglo está vacío, es requerido
        if (array.length === 1) return !!array[0].quantity; // Si solo hay un objeto, solo quantity es requerido

        // Si hay más de un objeto, verificar que todos los objetos tengan quantity y points
        return array.every(
          (item) =>
            item.quantity !== null &&
            item.quantity !== undefined &&
            item.quantity !== '' &&
            !Number.isNaN(item.quantity) &&
            item.points
        );
      })
      .required('Debe cargar al menos un punto de programa'),
    otherwise: Yup.array()
  })
});

const hasInvalidProgramPoint = (programPoint) =>
  programPoint?.code === null ||
  programPoint?.code === '' ||
  programPoint?.subCode === null ||
  programPoint?.subCode === '' ||
  programPoint?.cost === null ||
  programPoint?.cost === '' ||
  programPoint?.cost?.nombre === '' ||
  programPoint?.code?.nombre === '' ||
  programPoint?.subCode?.nombre === '';

const mapProgramPoint = (
  {
    cantidad,
    claseCosto,
    codigo,
    descripcion,
    id,
    idOperativo,
    idParteTrabajoDiario,
    puntoDePrograma,
    subcodigo
  },
  quantity,
  dailyWorkReportId
) => ({
  quantity: cantidad,
  points: puntoDePrograma || '',
  idPartePuntoPrograma: id,
  idParteDiario: idParteTrabajoDiario || dailyWorkReportId,
  cost: claseCosto
    ? {
        costoClase: claseCosto?.split('-')[1],
        id: claseCosto,
        nombre: claseCosto,
        label: claseCosto,
        fase: claseCosto?.split('-')[1]
      }
    : null,
  code: claseCosto ? { nombre: codigo, id: codigo, label: codigo } : null,
  subCode: subcodigo ? { nombre: subcodigo, id: subcodigo, label: subcodigo } : null,
  detail: descripcion,
  idOperativo: idOperativo || 0
});

const getCosts = async (idParteDiario, daily) =>
  adapterDefaultValueCS({
    id: idParteDiario,
    body: {
      tipoAsignacion: daily?.tipoAsignacion,
      numeroContrato: daily?.numeroContrato,
      numeroPosicion: Number(daily?.positionName.split(' ')[0]),
      numeroLinea: Number(daily?.lineName.split(' ')[0])
    }
  });

const shouldShowPPButtonForApprover = (
  isApprover,
  notificaPorPuntosPrograma,
  variosPuntosPrograma,
  puntoPrograma,
  tienePuntosPrograma,
  tipoUbicacionEnum
) =>
  isApprover &&
  ((notificaPorPuntosPrograma && !variosPuntosPrograma && puntoPrograma === null) ||
    tienePuntosPrograma) &&
  tipoUbicacionEnum === ubicationTypeEnum.pozo;

const shouldShowPPButtonForExtendView = (
  isFromExtendView,
  puedeNotificarPorPuntoPrograma,
  notificaPorPuntosPrograma
) => isFromExtendView && puedeNotificarPorPuntoPrograma && !notificaPorPuntosPrograma;

const handleCancel = async (dailyWorkReportId, comment) =>
  cancelQuantity({
    idParteDiario: dailyWorkReportId,
    comentario: comment
  });

const handleInspectorRectifyLine = async (dailyWorkReportId, newQuantity, comment) =>
  rectifyALine({
    id: dailyWorkReportId,
    cantidad: Number(newQuantity),
    comentario: comment
  });

const handleInspectorRectifyProgramPoint = async (dailyWorkReportId, values) => {
  const dataToSendRectify = {
    comentario: values?.comment,
    parteDiarioPuntosPrograma: values?.programPoints?.map(
      ({ quantity: quantityValue, points, idPartePuntoPrograma, idParteDiario }) => ({
        puntosDePrograma: points === '' ? null : Number(points),
        cantidad: Number(quantityValue),
        idPartePuntoPrograma: idPartePuntoPrograma || 0,
        idParteDiario: idParteDiario || dailyWorkReportId
      })
    )
  };

  return rectiyProgramPoint(dailyWorkReportId, dataToSendRectify);
};

const handleApproverRectifyProgramPoint = async (dailyWorkReportId, values) => {
  const dataToSend = values?.programPoints?.map(
    ({
      quantity: cantidad,
      points,
      idPartePuntoPrograma,
      idParteDiario,
      idOperativo,
      cost,
      code,
      subCode,
      detail
    }) => ({
      puntosDePrograma: points === '' ? null : Number(points),
      cantidad: Number(cantidad),
      idPartePuntoPrograma: idPartePuntoPrograma || 0,
      idParteDiario: dailyWorkReportId || idParteDiario,
      idOperativo: idOperativo || 0,
      claseCosto: cost?.nombre.split([' - '])[0],
      fase: cost?.fase.trim(),
      codigoCosto: code?.label,
      subCodigo: subCode?.label,
      descripcion: detail
    })
  );

  return approverRectifyLineWithPP(dailyWorkReportId, {
    comentario: values?.comment,
    parteDiarioPuntosPrograma: dataToSend
  });
};

const handleApproverRectifyLine = async (dailyWorkReportId, newQuantity, comment) =>
  approverRectifyALine({
    id: dailyWorkReportId,
    cantidad: Number(newQuantity),
    comentario: comment
  });

const applyClassnameInspector = (isInspector) => (isInspector ? 'cancel-quantity-label ' : '');

const EditQuantityModal = (props) => {
  const {
    show,
    onHide,
    onAccept,
    dailyWorkReportId,
    positionName,
    lineName,
    um,
    quantity,
    date,
    yClickCertification,
    estadoEnum,
    tienePuntosPrograma,
    tipoUbicacionEnum,
    notificaPorPuntosPrograma,
    variosPuntosPrograma,
    puntoPrograma,
    puedeNotificarPorPuntoPrograma,
    tipoAsignacion,
    defaultValue,
    numeroContrato,
    variosValoresOperativos,
    codigoOperativo
  } = props;

  const [disableButton, setDisableButton] = useState(false);
  const { day, month, year } = formatDateToSpanish(date);
  const { setNewToast } = useToast();
  const { setLoader } = useContext(LoaderContext);
  const [isCancel, setIsCancel] = useState(false);
  const [isProgramPoint, setIsProgramPoint] = useState(false);
  const [quantityTotalReturn, setQuantityTotalReturn] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const isInspector = checkRol(inspector);

  const isApprover = checkRol(aprobador);

  const dontShowTextArea =
    isApprover && tipoUbicacionEnum === ubicationTypeEnum.locacion && !yClickCertification;

  const { values, touched, errors, setFieldValue, resetForm } = useFormik({
    initialValues: {
      quantity,
      newQuantity: quantity,
      requireAccordance: true,
      requestAttachFile: false,
      comment: '',
      programPoints: [
        {
          quantity: '',
          points: '',
          idPartePuntoPrograma: null,
          idParteDiario: null,
          cost: null,
          code: null,
          subCode: null,
          detail: null
        }
      ],
      totalValue: 0,
      isPP: false
    },
    validationSchema
  });

  const handleIsProgramPoint = (res) => {
    if (res.length !== 0) {
      setFieldValue('isPP', true);
      setIsProgramPoint(true);
    }
  };

  const calculateTotalValue = (adapterPP) =>
    adapterPP?.reduce((acc, curr) => acc + Number(curr.quantity), 0) || 0;

  useEffect(() => {
    const getDataPP = async () => {
      try {
        setLoading(true);

        const res = await getProgramPoints(dailyWorkReportId);

        handleIsProgramPoint(res);

        const adapterPP = res?.map((pp) => mapProgramPoint(pp, quantity, dailyWorkReportId));

        const totalValue = calculateTotalValue(adapterPP);

        setFieldValue('totalValue', totalValue);
        setQuantityTotalReturn(totalValue);
        let costs;

        if (!variosValoresOperativos && !codigoOperativo && defaultValue) {
          costs = await getCosts(dailyWorkReportId, {
            tipoAsignacion,
            numeroContrato,
            positionName,
            lineName
          });
          if (adapterPP.length > 0) {
            adapterPP[0] = {
              ...adapterPP[0],
              cost: costs?.cost,
              code: costs?.code,
              subCode: costs?.subCode,
              idParteDiario: dailyWorkReportId
            };
          } else {
            adapterPP.push({
              ...adapterPP[0],
              quantity,
              idParteDiario: dailyWorkReportId,
              cost: costs?.cost,
              code: costs?.code,
              subCode: costs?.subCode
            });
          }
        }

        setFieldValue(
          'programPoints',
          adapterPP?.length === 0
            ? [
                {
                  quantity: '',
                  points: ''
                }
              ]
            : adapterPP
        );
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setNewToast({
          type: 'ERROR',
          message:
            error?.data?.errorMessage || 'No pudimos procesar la solicitud, intente nuevamente.'
        });
      }
    };

    if (show) {
      setFieldValue('newQuantity', quantity);

      if (tipoUbicacionEnum === ubicationTypeEnum.pozo) {
        getDataPP();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, quantity]);

  useEffect(() => {
    const hasCompleteData = values.programPoints.every(
      (PP) =>
        Object.prototype.hasOwnProperty.call(PP, 'code') &&
        Object.prototype.hasOwnProperty.call(PP, 'subCode') &&
        Object.prototype.hasOwnProperty.call(PP, 'cost')
    );

    if (hasCompleteData) {
      setShowAlert(values.programPoints.some(hasInvalidProgramPoint));
    } else {
      setShowAlert(true);
    }
  }, [values.programPoints]);

  useEffect(() => {
    if (!!Object.values(errors).length || !!Object.values(touched).length) {
      setDisableButton(true);
    } else {
      setDisableButton(false);
    }
  }, [errors, touched]);

  const handleOnClick = async () => {
    await setFieldValue('isPP', true);
    setIsProgramPoint((state) => !state);
  };

  const onExit = () => {
    setIsProgramPoint(false);
    resetForm();
    onHide();
  };

  const handleCommunicate = async () => {
    try {
      setLoader(true);
      setDisableButton(true);
      let response;

      // Los 3 primeros if son para cuando es inspector, los demas para el aprobador
      if (isCancel) {
        response = await handleCancel(dailyWorkReportId, values?.comment);
      } else if (isInspector && !isProgramPoint) {
        response = await handleInspectorRectifyLine(
          dailyWorkReportId,
          values.newQuantity,
          values?.comment
        );
      } else if (isInspector && isProgramPoint) {
        response = await handleInspectorRectifyProgramPoint(dailyWorkReportId, values);
      } else if (isProgramPoint) {
        response = await handleApproverRectifyProgramPoint(dailyWorkReportId, values);
      } else {
        response = await handleApproverRectifyLine(
          dailyWorkReportId,
          values?.newQuantity,
          values?.comment
        );
      }

      setNewToast({
        type: 'SUCCESS',
        message: response.mensaje
      });
      onAccept({
        isCancel,
        newQuantity: isProgramPoint ? values?.totalValue : +values.newQuantity
      });
      onExit();
    } catch (err) {
      setNewToast({
        type: 'ERROR',
        message: err.message
      });
    } finally {
      setLoader(false);
      setDisableButton(false);
    }
  };

  const isFromExtendView = window.location.pathname.includes('/approver/weekly-work-report/');

  const showPPButton =
    shouldShowPPButtonForApprover(
      isApprover,
      notificaPorPuntosPrograma,
      variosPuntosPrograma,
      puntoPrograma,
      tienePuntosPrograma,
      tipoUbicacionEnum
    ) ||
    shouldShowPPButtonForExtendView(
      isFromExtendView,
      puedeNotificarPorPuntoPrograma,
      notificaPorPuntosPrograma
    );

  return (
    <ModalGeneric
      backdrop="static"
      infoModal
      onHide={onExit}
      primaryAction={() => {
        handleCommunicate();
      }}
      primaryActionDisabled={
        disableButton ||
        (!values?.newQuantity && !isCancel) ||
        (!dontShowTextArea && values?.comment === '') ||
        (isInspector && !isProgramPoint && (values.newQuantity === 0 || values.newQuantity === '0'))
      }
      primaryActionLabel="Comunicar"
      secondaryAction={onExit}
      secondaryActionLabel="Salir"
      show={show}
      size="xl"
      title="Editar"
    >
      <>
        {loading && <Loader show={loading} text="CARGANDO ..." />}
        {isProgramPoint ? (
          <>
            <ProgramPointList
              disabledInputPP={false}
              disabledInputQuantity={false}
              displayButtons={!isInspector}
              editCSA={!isInspector}
              idParteDiario={dailyWorkReportId}
              line={{
                lineName,
                positionName,
                estadoEnum,
                tienePuntosPrograma,
                unidadMedida: um
              }}
              loading={loading}
              quantityTotalReturn={quantityTotalReturn}
              selectedDate={date}
              setFieldValue={setFieldValue}
              showAlert={showAlert}
              showRectifyQuantityButton={false}
              showTitle
              tipoAsignacion={tipoAsignacion}
              values={values}
              viewCSA
            />

            <div className="label-switch-textarea">
              {isInspector && (
                <label className="cancel-quantity-label">
                  Rechazar cantidad
                  <Switch
                    checked={isCancel}
                    falseLabel="NO"
                    name="cancelQuantity"
                    onChange={({ target }) => setIsCancel(!target.checked)}
                    size="sm"
                    trueLabel="SÍ"
                  />
                </label>
              )}
              {!dontShowTextArea && (
                <TextArea
                  className={`required-field textarea ${applyClassnameInspector(isInspector)}`}
                  errors="invalido"
                  isInvalid={false}
                  label="Comentario"
                  maxLength={512}
                  name="comments"
                  onChange={(e) => {
                    setFieldValue('comment', e.target.value);
                  }}
                  placeholder="Motivo de la modificación"
                  resize={false}
                  rows={1}
                  value={values?.comment}
                />
              )}
            </div>
          </>
        ) : (
          <>
            <div className="text-center modal-info">
              <div className="d-flex justify-content-center flex-wrap">
                <span>&quot;{positionName}</span>
                <span>{lineName}</span>
                <span>
                  {um} {values?.quantity}&quot;
                </span>
              </div>
              Para el día {capitalize(day?.text)}, {day?.number} de {month} de {year}
            </div>
            <hr className="my-4" />
            <div className="d-flex align-items-end gap-4 mb-4">
              {!isProgramPoint && (
                <Input
                  className={isInspector || !dontShowTextArea ? '' : 'edit-quantity-input'}
                  disabled={isCancel}
                  label="Proponer nueva cantidad"
                  name="newQuantity"
                  onChange={(e) => {
                    const { value } = e.target;

                    if (validateZerosInput(value, 5)) {
                      setFieldValue('newQuantity', value);
                    }
                  }}
                  type="number"
                  value={values?.newQuantity}
                />
              )}
              {showPPButton && (
                <div className="d-flex ml-3 mb-2 align-items-end">
                  <ComponentWithTooltip
                    id="hidden-tooltip-pp"
                    tooltipText="Activar Puntos de Programa"
                  >
                    <div>
                      <button aria-label="Punto Programa" onClick={handleOnClick} type="button">
                        <TbCircleLetterP color="#222222" size={25} />
                      </button>
                    </div>
                  </ComponentWithTooltip>
                </div>
              )}
              {isInspector && (
                <label className="cancel-quantity-label">
                  Rechazar cantidad
                  <Switch
                    checked={isCancel}
                    falseLabel="NO"
                    name="cancelQuantity"
                    onChange={({ target }) => setIsCancel(!target.checked)}
                    size="sm"
                    trueLabel="SÍ"
                  />
                </label>
              )}
            </div>
            {!dontShowTextArea && (
              <TextArea
                className={`required-field isInspector ${
                  isInspector ? 'cancel-quantity-label' : ''
                }`}
                errors="invalido"
                isInvalid={false}
                label="Comentario"
                maxLength={512}
                name="comments"
                onChange={(e) => {
                  setFieldValue('comment', e.target.value);
                }}
                placeholder="Motivo de la modificación"
                resize={false}
                rows={4}
                value={values?.comment}
              />
            )}
          </>
        )}
      </>
    </ModalGeneric>
  );
};

export default EditQuantityModal;

EditQuantityModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  onAccept: PropTypes.func,
  dailyWorkReportId: PropTypes.number,
  positionName: PropTypes.string,
  lineName: PropTypes.string,
  um: PropTypes.string,
  quantity: PropTypes.number,
  date: PropTypes.string,
  yClickCertification: PropTypes.bool,
  estadoEnum: PropTypes.number.isRequired,
  tienePuntosPrograma: PropTypes.bool.isRequired,
  tipoUbicacionEnum: PropTypes.number.isRequired,
  notificaPorPuntosPrograma: PropTypes.bool.isRequired,
  variosPuntosPrograma: PropTypes.bool.isRequired,
  puntoPrograma: PropTypes.string,
  tipoAsignacion: PropTypes.number.isRequired,
  puedeNotificarPorPuntoPrograma: PropTypes.bool.isRequired,
  defaultValue: PropTypes.bool,
  numeroContrato: PropTypes.number.isRequired,
  variosValoresOperativos: PropTypes.bool,
  codigoOperativo: PropTypes.string
};

EditQuantityModal.defaultProps = {
  show: false,
  onHide: () => {
    /* Empty */
  },
  onAccept: () => {
    /* Empty */
  },
  dailyWorkReportId: null,
  positionName: '',
  lineName: '',
  um: '',
  quantity: 0,
  date: '',
  yClickCertification: false,
  puntoPrograma: null,
  defaultValue: false,
  variosValoresOperativos: false,
  codigoOperativo: null
};
